/**
 * Minimal ULID. No dependency, works identically in a Worker, a browser and
 * React Native.
 *
 * Why ULIDs rather than UUIDs: they sort lexicographically by creation time,
 * which means the sync cursor is just "the last id I saw" and `?since=` is a
 * plain string comparison in SQL. They are generated client-side, so every
 * create is idempotent and a retry after a dropped response can never produce
 * a duplicate item.
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32, no I/L/O/U
const ENCODING_LEN = 32;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

/** Monotonic guard: two calls in the same millisecond must still sort correctly. */
let lastTime = -1;
let lastRandom: number[] = [];

function encodeTime(now: number): string {
  if (!Number.isFinite(now) || now < 0 || now > 281_474_976_710_655) {
    throw new RangeError(`ulid: timestamp out of range: ${now}`);
  }
  let out = '';
  let t = now;
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    out = ENCODING[t % ENCODING_LEN] + out;
    t = Math.floor(t / ENCODING_LEN);
  }
  return out;
}

function randomChars(): number[] {
  const bytes = new Uint8Array(RANDOM_LEN);
  crypto.getRandomValues(bytes);
  // Map each byte into the 32-char alphabet. Modulo bias is irrelevant here —
  // this is a collision-avoidance nonce, not a secret.
  return Array.from(bytes, (b) => b % ENCODING_LEN);
}

/** Increment the random field in place so same-millisecond ids stay ordered. */
function bumpRandom(chars: number[]): number[] {
  const out = chars.slice();
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] < ENCODING_LEN - 1) {
      out[i]++;
      return out;
    }
    out[i] = 0;
  }
  // Overflowed a 80-bit random field within one millisecond. Not reachable in
  // practice; regenerate rather than return a duplicate.
  return randomChars();
}

export function ulid(now: number = Date.now()): string {
  let random: number[];
  if (now === lastTime) {
    random = bumpRandom(lastRandom);
  } else {
    random = randomChars();
  }
  lastTime = now;
  lastRandom = random;
  return encodeTime(now) + random.map((n) => ENCODING[n]).join('');
}

/** Extract the creation timestamp (epoch ms) encoded in a ULID. */
export function ulidTime(id: string): number {
  if (id.length !== TIME_LEN + RANDOM_LEN) {
    throw new TypeError(`ulid: malformed id (length ${id.length})`);
  }
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) {
    const v = ENCODING.indexOf(id[i]);
    if (v === -1) throw new TypeError(`ulid: invalid character "${id[i]}"`);
    t = t * ENCODING_LEN + v;
  }
  return t;
}

export function isUlid(id: unknown): id is string {
  if (typeof id !== 'string' || id.length !== TIME_LEN + RANDOM_LEN) return false;
  for (const ch of id) if (ENCODING.indexOf(ch) === -1) return false;
  return true;
}
